import {
  ArrowRight,
  Baby,
  CalendarBlank,
  IdentificationCard,
  MapPin,
  Minus,
  Plus,
  Sparkle,
  UserCircle,
  X,
} from '@phosphor-icons/react';
import { useContext, useId, useRef, useState, type ChangeEvent, type FC, type FormEvent } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import { VISA_IMAGES } from '@/app/agent/media.ts';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { num, str } from '@/app/lib/a2ui/props.ts';
import { A2uiSurfaceContext } from '@/app/lib/a2ui/surface-context.ts';
import { tripBookingMessages } from './TripBooking.tsx';
import { travelPassportMessages } from './TravelPassport.tsx';

const PANEL_FIELD_CLASS =
  'w-full rounded-input border border-border bg-muted/40 px-3 py-2.5 text-base text-foreground transition-colors duration-150 placeholder:text-muted-foreground-subtle focus-visible:border-primary focus-visible:outline-none';

const GENDER_OPTION_CLASS =
  'flex-1 rounded-input border border-border bg-muted/40 px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors duration-150 [touch-action:manipulation] hover:border-primary/60 hover:text-foreground data-[selected=true]:border-primary data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';

const VISA_OPTION_CLASS =
  'group flex min-w-0 flex-1 items-center gap-3 rounded-[15px] border border-border bg-muted/30 p-3 text-left transition duration-150 [touch-action:manipulation] hover:border-primary/60 data-[selected=true]:border-primary data-[selected=true]:bg-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';

const CHILDREN_MAX = 8;
const STEPPER_BUTTON_CLASS =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-foreground transition-colors duration-150 [touch-action:manipulation] hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-border disabled:hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';

const surprisePopupMessages = defineMessages({
  missingIdentity: {
    id: 'surpriseTravellerPopup.missingIdentity',
    defaultMessage: 'Complete your name, demo passport number, date of birth, departure city and visa lane to continue.',
    description: 'Validation message for the automatic Surprise me traveller popup',
  },
});

const GENDER_OPTIONS: Array<{ id: 'male' | 'female'; label: MessageDescriptor }> = [
  { id: 'male', label: travelPassportMessages.genderMale },
  { id: 'female', label: travelPassportMessages.genderFemale },
];

const VISA_OPTIONS: Array<{
  id: 'yes' | 'no' | 'unsure';
  label: MessageDescriptor;
  detail: MessageDescriptor;
  image: string;
}> = [
  {
    id: 'yes',
    label: travelPassportMessages.visaYes,
    detail: travelPassportMessages.visaYesDetail,
    image: VISA_IMAGES.yes,
  },
  {
    id: 'no',
    label: travelPassportMessages.visaNo,
    detail: travelPassportMessages.visaNoDetail,
    image: VISA_IMAGES.no,
  },
  {
    id: 'unsure',
    label: travelPassportMessages.visaUnsure,
    detail: travelPassportMessages.visaUnsureDetail,
    image: VISA_IMAGES.unsure,
  },
];

export const SurpriseTravellerPopup: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const api = useContext(A2uiSurfaceContext);
  const popupId = useId();
  const firstNameRef = useRef<HTMLInputElement>(null);
  const surnameRef = useRef<HTMLInputElement>(null);
  const passportNumberRef = useRef<HTMLInputElement>(null);
  const dateOfBirthRef = useRef<HTMLInputElement>(null);
  const originRef = useRef<HTMLInputElement>(null);
  const [showErrors, setShowErrors] = useState(false);

  const title = str(node.props.title) || intl.formatMessage(travelPassportMessages.identityPrompt);
  const subtitle = str(node.props.subtitle) || intl.formatMessage(travelPassportMessages.identityHint);
  const destinationId = str(node.props.destinationId);
  const destinationName = str(node.props.destinationName);
  const destinationCountry = str(node.props.destinationCountry);
  const city = str(node.props.city);
  const planId = str(node.props.planId);
  const planName = str(node.props.planName);
  const planType = str(node.props.planType);
  const style = str(node.props.style);
  const season = str(node.props.season);
  const seededOrigin = str(node.props.origin);
  const days = num(node.props.days) ?? 0;
  const budget = num(node.props.budget) ?? 0;

  const read = (path: string): string => str(api?.getValue(path));
  const write = (path: string, value: string): void => api?.setValue(path, value);

  const firstName = read('/firstName');
  const surname = read('/surname');
  const passportNumber = read('/passportNumber');
  const dateOfBirth = read('/dateOfBirth');
  const origin = read('/origin') || seededOrigin;
  const storedGender = read('/gender');
  const gender: 'male' | 'female' = storedGender === 'female' ? 'female' : 'male';
  const visa = read('/visa');
  const childrenRaw = Number.parseInt(read('/children'), 10);
  const childCount = Number.isFinite(childrenRaw)
    ? Math.min(Math.max(childrenRaw, 0), CHILDREN_MAX)
    : 0;

  const handleText =
    (path: string) =>
    (event: ChangeEvent<HTMLInputElement>): void => {
      write(path, event.target.value);
      setShowErrors(false);
    };

  const choose = (path: string, value: string): void => {
    write(path, value);
    api?.commitValue();
    setShowErrors(false);
  };

  const stepChildren = (delta: number): void => {
    choose('/children', String(Math.min(Math.max(childCount + delta, 0), CHILDREN_MAX)));
  };

  const cancel = (reason: 'closed' | 'declined'): void => {
    api?.dispatch('cancelSurprise', { reason });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const valid =
      firstName.trim().length > 0 &&
      surname.trim().length > 0 &&
      passportNumber.trim().length > 0 &&
      dateOfBirth.trim().length > 0 &&
      origin.trim().length > 0 &&
      visa.length > 0;

    if (!valid) {
      setShowErrors(true);
      if (firstName.trim().length === 0) {
        firstNameRef.current?.focus();
      } else if (surname.trim().length === 0) {
        surnameRef.current?.focus();
      } else if (passportNumber.trim().length === 0) {
        passportNumberRef.current?.focus();
      } else if (dateOfBirth.trim().length === 0) {
        dateOfBirthRef.current?.focus();
      } else if (origin.trim().length === 0) {
        originRef.current?.focus();
      }
      return;
    }

    setShowErrors(false);
    api?.commitValue();
    api?.dispatch('completeSurpriseTraveller', {
      firstName,
      surname,
      passportNumber,
      dateOfBirth,
      gender,
      children: String(childCount),
      visa,
      origin,
      destinationId,
      destinationName,
      destinationCountry,
      city,
      planId,
      planName,
      planType,
      style,
      season,
      days,
      budget,
    });
  };

  return (
    <div
      className="surprise-traveller-popup mx-auto w-full max-w-[1240px] rounded-[15px] border border-primary/20 bg-card p-5 text-foreground shadow-[0_25px_70px_-15px_hsl(var(--foreground)/0.12)]"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${popupId}-title`}
      aria-describedby={`${popupId}-subtitle`}
      data-wayfarer-surprise-popup
      data-wayfarer-step="5"
      data-wayfarer-step-available-through="5"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1
            id={`${popupId}-title`}
            className="text-balance text-3xl font-bold leading-[1.08] tracking-tighter text-foreground sm:text-4xl"
          >
            {title}
          </h1>
          <p id={`${popupId}-subtitle`} className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
            {subtitle}
          </p>
        </div>
        <button
          type="button"
          onClick={() => cancel('closed')}
          aria-label={intl.formatMessage(travelPassportMessages.back)}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-muted/40 text-muted-foreground transition-colors hover:border-primary hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          <X size={18} weight="bold" aria-hidden="true" />
        </button>
      </div>

      <div className="mt-5">
        <form
          className="flex flex-col overflow-hidden rounded-[15px] border border-border bg-muted/30 p-5"
          onSubmit={handleSubmit}
          noValidate
        >
          <div>
            <h2 className="mt-[25px] text-xl font-bold leading-tight text-foreground">
              {intl.formatMessage(travelPassportMessages.identityPrompt)}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {intl.formatMessage(travelPassportMessages.identityHint)}
            </p>
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-foreground">
                {intl.formatMessage(travelPassportMessages.firstName)}
              </span>
              <input
                ref={firstNameRef}
                type="text"
                value={firstName}
                onChange={handleText('/firstName')}
                onBlur={() => api?.commitValue()}
                placeholder={intl.formatMessage(travelPassportMessages.firstNamePlaceholder)}
                autoComplete="given-name"
                aria-invalid={showErrors && firstName.trim().length === 0}
                className={PANEL_FIELD_CLASS}
              />
            </label>

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-foreground">
                {intl.formatMessage(travelPassportMessages.surname)}
              </span>
              <input
                ref={surnameRef}
                type="text"
                value={surname}
                onChange={handleText('/surname')}
                onBlur={() => api?.commitValue()}
                placeholder={intl.formatMessage(travelPassportMessages.surnamePlaceholder)}
                autoComplete="family-name"
                aria-invalid={showErrors && surname.trim().length === 0}
                className={PANEL_FIELD_CLASS}
              />
            </label>

            <label className="block">
              <span className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-foreground">
                <IdentificationCard size={15} weight="duotone" aria-hidden="true" />
                {intl.formatMessage(travelPassportMessages.passportNumber)}
              </span>
              <input
                ref={passportNumberRef}
                type="text"
                inputMode="numeric"
                value={passportNumber}
                onChange={handleText('/passportNumber')}
                onBlur={() => api?.commitValue()}
                placeholder={intl.formatMessage(travelPassportMessages.passportNumberPlaceholder)}
                autoComplete="off"
                spellCheck={false}
                aria-describedby={`${popupId}-passport-hint`}
                aria-invalid={showErrors && passportNumber.trim().length === 0}
                className={PANEL_FIELD_CLASS}
              />
              <span id={`${popupId}-passport-hint`} className="mt-1.5 block text-[11px] leading-snug text-muted-foreground-subtle">
                {intl.formatMessage(travelPassportMessages.passportNumberHint)}
              </span>
            </label>

            <label className="block">
              <span className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-foreground">
                <CalendarBlank size={15} weight="duotone" aria-hidden="true" />
                {intl.formatMessage(travelPassportMessages.dateOfBirth)}
              </span>
              <input
                ref={dateOfBirthRef}
                type="date"
                value={dateOfBirth}
                onChange={handleText('/dateOfBirth')}
                onBlur={() => api?.commitValue()}
                autoComplete="off"
                aria-describedby={`${popupId}-birth-hint`}
                aria-invalid={showErrors && dateOfBirth.trim().length === 0}
                className={PANEL_FIELD_CLASS}
              />
              <span id={`${popupId}-birth-hint`} className="mt-1.5 block text-[11px] leading-snug text-muted-foreground-subtle">
                {intl.formatMessage(travelPassportMessages.dateOfBirthHint)}
              </span>
            </label>

            <div className="sm:col-span-2">
              <span className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-foreground">
                <UserCircle size={15} weight="duotone" aria-hidden="true" />
                {intl.formatMessage(travelPassportMessages.gender)}
              </span>
              <div className="flex gap-3" role="group" aria-label={intl.formatMessage(travelPassportMessages.gender)}>
                {GENDER_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    data-selected={gender === option.id}
                    aria-pressed={gender === option.id}
                    onClick={() => choose('/gender', option.id)}
                    className={GENDER_OPTION_CLASS}
                  >
                    {intl.formatMessage(option.label)}
                  </button>
                ))}
              </div>
            </div>

            <label className="block">
              <span className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-foreground">
                <MapPin size={15} weight="duotone" aria-hidden="true" />
                {intl.formatMessage(travelPassportMessages.origin)}
              </span>
              <input
                ref={originRef}
                type="text"
                value={origin}
                onChange={handleText('/origin')}
                onBlur={() => api?.commitValue()}
                placeholder={intl.formatMessage(travelPassportMessages.originPlaceholder)}
                autoComplete="address-level2"
                aria-invalid={showErrors && origin.trim().length === 0}
                className={PANEL_FIELD_CLASS}
              />
            </label>

            <div>
              <span className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-foreground">
                <Baby size={15} weight="duotone" aria-hidden="true" />
                {intl.formatMessage(travelPassportMessages.children)}
              </span>
              <div className="flex items-center gap-1 rounded-input border border-border bg-muted/40 px-1.5 py-1.5">
                <button
                  type="button"
                  onClick={() => stepChildren(-1)}
                  disabled={childCount === 0}
                  aria-label={intl.formatMessage(travelPassportMessages.childrenFewer)}
                  className={STEPPER_BUTTON_CLASS}
                >
                  <Minus size={15} weight="bold" aria-hidden="true" />
                </button>
                <span aria-live="polite" className="flex-1 text-center text-base font-bold tabular-nums text-foreground">
                  {childCount}
                </span>
                <button
                  type="button"
                  onClick={() => stepChildren(1)}
                  disabled={childCount === CHILDREN_MAX}
                  aria-label={intl.formatMessage(travelPassportMessages.childrenMore)}
                  className={STEPPER_BUTTON_CLASS}
                >
                  <Plus size={15} weight="bold" aria-hidden="true" />
                </button>
              </div>
              {childCount > 0 ? (
                <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground-subtle">
                  {intl.formatMessage(travelPassportMessages.childrenSome)}
                </p>
              ) : null}
            </div>

            <div className="sm:col-span-2">
              <span className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-foreground">
                <IdentificationCard size={15} weight="duotone" aria-hidden="true" />
                {intl.formatMessage(travelPassportMessages.visaPrompt)}
              </span>
              <p className="mb-3 text-[11px] leading-snug text-muted-foreground-subtle">
                {intl.formatMessage(travelPassportMessages.visaHint)}
              </p>
              <div className="grid gap-2 md:grid-cols-3" role="group" aria-label={intl.formatMessage(travelPassportMessages.visaPrompt)}>
                {VISA_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    data-selected={visa === option.id}
                    aria-pressed={visa === option.id}
                    onClick={() => choose('/visa', option.id)}
                    className={VISA_OPTION_CLASS}
                  >
                    <span className="h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-border bg-muted">
                      <img src={option.image} alt="" className="h-full w-full object-cover" loading="lazy" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-bold text-foreground">
                        {intl.formatMessage(option.label)}
                      </span>
                      <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                        {intl.formatMessage(option.detail)}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {showErrors &&
          (firstName.trim().length === 0 ||
            surname.trim().length === 0 ||
            passportNumber.trim().length === 0 ||
            dateOfBirth.trim().length === 0 ||
            origin.trim().length === 0 ||
            visa.length === 0) ? (
            <p
              className="mt-5 rounded-md border border-destructive/30 bg-destructive/5 px-3.5 py-3 text-sm font-medium text-destructive"
              role="alert"
            >
              {intl.formatMessage(surprisePopupMessages.missingIdentity)}
            </p>
          ) : null}

          <div className="order-first mt-0 rounded-[15px] border border-primary/20 bg-primary/5 p-5">
            <div className="grid gap-4 sm:grid-cols-4">
              <div className="min-w-0">
                <span className="flex items-center gap-1.5 font-consolas text-[10px] font-semibold uppercase tracking-caps text-muted-foreground-subtle">
                  <MapPin size={14} weight="duotone" aria-hidden="true" />
                  {intl.formatMessage(tripBookingMessages.destination)}
                </span>
                <p className="mt-1 text-lg font-bold leading-tight text-foreground">
                  <span translate="no">{destinationName}</span>
                  {destinationCountry ? (
                    <span className="text-muted-foreground">
                      {' · '}
                      <span translate="no">{destinationCountry}</span>
                    </span>
                  ) : null}
                </p>
                {city ? (
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                    <span translate="no">{city}</span>
                  </p>
                ) : null}
              </div>
              <div className="min-w-0">
                <span className="block font-consolas text-[10px] font-semibold uppercase tracking-caps text-muted-foreground-subtle">
                  {intl.formatMessage(tripBookingMessages.plan)}
                </span>
                <span className="mt-1 block text-sm font-bold text-foreground" translate="no">
                  {planName}
                </span>
              </div>
              <div className="min-w-0">
                <span className="block font-consolas text-[10px] font-semibold uppercase tracking-caps text-muted-foreground-subtle">
                  {intl.formatMessage(travelPassportMessages.durationRound)}
                </span>
                <span className="mt-1 block text-sm font-bold text-foreground">
                  {intl.formatMessage(tripBookingMessages.duration, { days })}
                </span>
              </div>
              {budget > 0 ? (
                <div className="min-w-0">
                  <span className="block font-consolas text-[10px] font-semibold uppercase tracking-caps text-muted-foreground-subtle">
                    {intl.formatMessage(tripBookingMessages.budget)}
                  </span>
                  <span className="mt-1 block text-sm font-bold text-foreground">
                    {intl.formatNumber(budget, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
                  </span>
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-auto flex flex-wrap items-center justify-end gap-4 pt-6">
            <button
              type="button"
              onClick={() => cancel('declined')}
              className="rounded-input px-2 py-3 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              {intl.formatMessage(travelPassportMessages.back)}
            </button>
            <button
              type="submit"
              className="flex items-center justify-center gap-2 rounded-input bg-primary px-5 py-3 text-sm font-bold text-primary-foreground transition duration-150 [touch-action:manipulation] hover:brightness-110 active:scale-[0.99] motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
            >
              {intl.formatMessage(travelPassportMessages.next)}
              <ArrowRight size={17} weight="bold" aria-hidden="true" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
